import { useRef } from 'react';
import { ImagePlus } from 'lucide-react';
import { PorteEmpresa, formatarCnpj, type EmpresaConfigDto, type EmpresaEntrada } from '@nexital/shared';
import { useDependencias } from '../../application/dependencias';
import { CampoConfig, Grupo, ouNulo, somenteDigitos, useCarga, useGravar } from './campos';

const PORTES = [
  { valor: PorteEmpresa.Microempresa, rotulo: 'Microempresa (ME)' },
  { valor: PorteEmpresa.PequenoPorte, rotulo: 'Empresa de pequeno porte (EPP)' },
  { valor: PorteEmpresa.Demais, rotulo: 'Demais (sem enquadramento)' },
];

type Dados = EmpresaConfigDto;
const porteDe = (valor: string) => (valor ? (Number(valor) as PorteEmpresa) : null);
type Definir = <K extends keyof Dados>(campo: K) => (valor: string) => void;

/** O que vai ao servidor: textos aparados (vazio vira null), CNPJ, CEP e CPF só com dígitos. */
function entrada(d: Dados): EmpresaEntrada {
  const { uuid: _u, logoUrl: _l, ...resto } = d;
  const limpos = Object.fromEntries(Object.entries(resto).map(([k, v]) => [k, typeof v === 'string' ? ouNulo(v) : v])) as EmpresaEntrada;
  return {
    ...limpos,
    nome: d.nome.trim(),
    cnpj: ouNulo(d.cnpj?.toUpperCase().replace(/[^0-9A-Z]/g, '')),
    cep: ouNulo(somenteDigitos(d.cep ?? '')),
    representanteCpf: ouNulo(somenteDigitos(d.representanteCpf ?? '')),
    uf: ouNulo(d.uf?.toUpperCase()),
  };
}

function Logo({ url, enviar }: { url: string | null; enviar: (f: File) => void }) {
  const seletor = useRef<HTMLInputElement>(null);
  return (
    <div className="config__logo">
      {url ? <img src={url} alt="Logo da empresa" /> : <span className="config__logo-vazio">Sem logo</span>}
      <button type="button" className="botao botao--secundario" onClick={() => seletor.current?.click()}>
        <ImagePlus size={16} aria-hidden /> Trocar a logo
      </button>
      <input ref={seletor} type="file" hidden accept="image/png,image/jpeg,image/webp,image/svg+xml" onChange={(e) => e.target.files?.[0] && enviar(e.target.files[0])} />
    </div>
  );
}

function Cadastro({ d, campo }: { d: Dados; campo: Definir }) {
  return (
    <Grupo titulo="Cadastro">
      <CampoConfig rotulo="Nome fantasia" valor={d.nome} definir={campo('nome')} largura="longa" />
      <CampoConfig rotulo="Razão social" valor={d.razaoSocial} definir={campo('razaoSocial')} largura="longa" />
      <CampoConfig rotulo="CNPJ" valor={d.cnpj && d.cnpj.length === 14 ? formatarCnpj(d.cnpj) : d.cnpj} definir={campo('cnpj')} />
      <CampoConfig rotulo="Inscrição estadual" valor={d.inscricaoEstadual} definir={campo('inscricaoEstadual')} />
      <CampoConfig rotulo="Inscrição municipal" valor={d.inscricaoMunicipal} definir={campo('inscricaoMunicipal')} />
      <label className="campo config__campo config__campo--media">
        <span>Porte</span>
        <select value={d.porte ?? ''} onChange={(e) => campo('porte')(e.target.value)}>
          <option value="">Não informado</option>
          {PORTES.map((p) => (
            <option key={p.valor} value={p.valor}>
              {p.rotulo}
            </option>
          ))}
        </select>
        <small>ME e EPP têm os benefícios da LC 123 nas licitações.</small>
      </label>
    </Grupo>
  );
}

function EnderecoContatos({ d, campo }: { d: Dados; campo: Definir }) {
  return (
    <>
      <Grupo titulo="Endereço">
        <CampoConfig rotulo="CEP" valor={d.cep} definir={campo('cep')} largura="curta" />
        <CampoConfig rotulo="Logradouro" valor={d.logradouro} definir={campo('logradouro')} largura="longa" />
        <CampoConfig rotulo="Número" valor={d.numero} definir={campo('numero')} largura="curta" />
        <CampoConfig rotulo="Complemento" valor={d.complemento} definir={campo('complemento')} />
        <CampoConfig rotulo="Bairro" valor={d.bairro} definir={campo('bairro')} />
        <CampoConfig rotulo="Município" valor={d.municipio} definir={campo('municipio')} />
        <CampoConfig rotulo="UF" valor={d.uf} definir={campo('uf')} largura="curta" />
      </Grupo>
      <Grupo titulo="Contatos">
        <CampoConfig rotulo="Telefone" tipo="tel" valor={d.telefone} definir={campo('telefone')} />
        <CampoConfig rotulo="E-mail" tipo="email" valor={d.email} definir={campo('email')} />
        <CampoConfig rotulo="Site" valor={d.site} definir={campo('site')} largura="longa" />
      </Grupo>
    </>
  );
}

function RepresentanteBanco({ d, campo }: { d: Dados; campo: Definir }) {
  return (
    <>
      <Grupo titulo="Representante legal">
        <CampoConfig rotulo="Nome" valor={d.representanteNome} definir={campo('representanteNome')} largura="longa" />
        <CampoConfig rotulo="CPF" valor={d.representanteCpf} definir={campo('representanteCpf')} />
        <CampoConfig rotulo="Cargo" valor={d.representanteCargo} definir={campo('representanteCargo')} />
        <CampoConfig rotulo="E-mail" tipo="email" valor={d.representanteEmail} definir={campo('representanteEmail')} />
      </Grupo>
      <Grupo titulo="Dados bancários (só administradores veem)">
        <CampoConfig rotulo="Banco" valor={d.banco} definir={campo('banco')} />
        <CampoConfig rotulo="Agência" valor={d.agencia} definir={campo('agencia')} largura="curta" />
        <CampoConfig rotulo="Conta" valor={d.conta} definir={campo('conta')} />
      </Grupo>
    </>
  );
}

/** Dados da empresa (ADR-0022): só o administrador entra; grava com a versão (alterado por outra pessoa, avisa). */
export function SecaoEmpresa() {
  const { configuracoes } = useDependencias();
  const { dados, erro, recarregar, definir } = useCarga(configuracoes.empresa);
  const { gravar, gravando } = useGravar();
  if (erro) return <p className="cartao-acesso__erro">{erro}</p>;
  if (!dados) return <p>Carregando…</p>;
  const campo: Definir = (nome) => (valor) => definir({ ...dados, [nome]: nome === 'porte' ? porteDe(valor) : valor });
  const salvar = async () => {
    if (await gravar(() => configuracoes.salvarEmpresa(entrada(dados)), 'Dados da empresa salvos.')) await recarregar();
  };
  const enviarLogo = async (arquivo: File) => {
    if (await gravar(() => configuracoes.enviarLogo(arquivo), 'Logo atualizada.')) await recarregar();
  };
  return (
    <form
      className="config__formulario"
      onSubmit={(e) => {
        e.preventDefault();
        void salvar();
      }}
    >
      <Logo url={dados.logoUrl} enviar={(f) => void enviarLogo(f)} />
      <Cadastro d={dados} campo={campo} />
      <EnderecoContatos d={dados} campo={campo} />
      <RepresentanteBanco d={dados} campo={campo} />
      <div className="config__rodape">
        <button type="submit" className="botao botao--primario" disabled={gravando || dados.nome.trim().length < 2}>
          {gravando ? 'Salvando…' : 'Salvar'}
        </button>
      </div>
    </form>
  );
}
